import * as trace from 'owa-trace';
import ClientItem from 'owa-mail-store/lib/store/schema/ClientItem';
import CollectionChange from 'owa-mail-store/lib/store/schema/CollectionChange';
import conversationCache from 'owa-mail-store/lib/store/conversationCache';
import ConversationNode from 'owa-service/lib/contract/ConversationNode';
import ConversationReadingPaneNode from 'owa-mail-store/lib/store/schema/ConversationReadingPaneNode';
import ConversationSortOrder from 'owa-service/lib/contract/ConversationSortOrder';
import createEmptyConversationItemParts from '../utils/createEmptyConversationItemParts';
import GetConversationItemsResponseMessage from 'owa-service/lib/contract/GetConversationItemsResponseMessage';
import Item from 'owa-service/lib/contract/Item';
import loadConversation, { MAX_ITEMS_ALLOWED_TO_RETRIEVE } from './loadConversation';
import mailStore from 'owa-mail-store/lib/store/Store';
import Message from 'owa-service/lib/contract/Message';
import preserveItemDataInCache from 'owa-mail-store/lib/utils/preserveItemDataInCache';
import { action, select } from 'satcheljs/lib/legacy';
import { lazyPostLoadConversation } from 'mini-mail-reading-pane-post-load-conversation';
import { MruCache } from 'owa-mru-cache';
import { ObservableMap } from 'mobx';
import ConversationItemParts, {
    HasLoadState,
} from 'owa-mail-store/lib/store/schema/ConversationItemParts';

interface IdProvider<T> {
    (object: T): string;
}

const itemMandatoryProperties = [
    'ItemClass',
    'LastModifiedTime',
    'DateTimeReceived',
    'ParentFolderId',
    'Categories',
    'SystemCategories',
    'Hashtags',
    'PredictedActionReasons',
    'Flag',
    'IsRead',
    'Size',
    'IsClutter',
    'IconIndex',
    'IsDraft',
    'SentTime',
];

const conversationNodeIdProvider: IdProvider<ConversationNode> = (
    conversationNode: ConversationNode
) => conversationNode.InternetMessageId;

const itemIdProvider: IdProvider<Item> = (item: Item) => item.ItemId.Id;

/** Generate array of ids from array of objects. */
function generateIdsFromObjects<T>(objects: T[], idProvider: IdProvider<T>): string[] {
    const ids: string[] = [];
    objects.forEach(object => {
        ids.push(idProvider(object));
    });
    return ids;
}

/** Compare array of ids between old and new. */
function checkIfDifferentIds(oldIds: string[], newIds: string[]): boolean {
    if (oldIds.length !== newIds.length) {
        return true;
    }

    for (let i = 0; i < oldIds.length; i++) {
        if (oldIds[i] !== newIds[i]) {
            return true;
        }
    }

    return false;
}

/** Get the old ids that are not included in the array of new ids */
function getAdditionalOldIds(oldIds: string[], newIds: string[]): string[] {
    const additionalOldIds: string[] = [];
    oldIds.forEach(oldId => {
        if (newIds.indexOf(oldId) < 0) {
            additionalOldIds.push(oldId);
        }
    });
    return additionalOldIds;
}

/** Clear and refill with new ids if they are different from old ids. */
/** Also return the old ids that are not included in the new ids. */
function resetIdsIfDifferent<T>(
    oldIds: string[],
    objects: T[],
    idProvider: IdProvider<T>
): [boolean, string[]] {
    const newIds = generateIdsFromObjects(objects, idProvider);
    if (checkIfDifferentIds(oldIds, newIds)) {
        const additionalOldIds = getAdditionalOldIds(oldIds, newIds);
        // Clear and refill.
        const oldCount = oldIds.length;
        oldIds.splice(0, oldCount, ...newIds);
        return [true, additionalOldIds];
    }
    return [false, []];
}

function checkIfCollectionChanged<T>(change: CollectionChange<T>): boolean {
    return change.added.length > 0 || change.removed.length > 0 || change.modified.length > 0;
}

function cleanUpItem(itemId: string, itemsState: ObservableMap<string, Item>) {
    itemsState.delete(itemId);
}

function cleanUpConversationNode(
    nodeId: string,
    conversationNodesState: ObservableMap<string, ConversationReadingPaneNode>,
    itemsState: ObservableMap<string, Item>
) {
    const nodeToRemove = conversationNodesState.get(nodeId);
    nodeToRemove.itemIds.forEach(itemId => {
        cleanUpItem(itemId, itemsState);
    });
    conversationNodesState.delete(nodeId);
}

/** @returns collection change of ids for conversation nodes. */
function mergeConversationNodes(
    oldIds: string[],
    newConversationNodes: ConversationNode[],
    conversationNodesState: ObservableMap<string, ConversationReadingPaneNode>,
    itemsState: ObservableMap<string, Item>,
    conversationId: string
): CollectionChange<string> {
    const additionalOldIds = resetIdsIfDifferent(
        oldIds,
        newConversationNodes,
        conversationNodeIdProvider
    )[1];
    const nodeIdCollectionChange: CollectionChange<string> = {
        added: [],
        removed: [],
        modified: [],
    };
    // Merge each new node with the old.
    newConversationNodes.forEach(newNode => {
        const nodeId = conversationNodeIdProvider(newNode);
        const oldNode = conversationNodesState.get(nodeId);
        let nodeToMerge: ConversationReadingPaneNode;

        if (!oldNode) {
            // This is the case where a new node is added.
            // Create and save a node to merge.
            nodeToMerge = {
                conversationId: conversationId,
                internetMessageId: nodeId,
                itemIds: [],
                quotedTextList: newNode.QuotedTextList,
                hasQuotedText: newNode.HasQuotedText,
            };
            conversationNodesState.set(nodeId, nodeToMerge);
            nodeIdCollectionChange.added.push(nodeId);
        } else {
            nodeToMerge = oldNode;
        }

        // Merge items for each new node.
        const oldItemIds = nodeToMerge.itemIds;
        const isItemArrayDifferent = mergeItems(oldItemIds, newNode.Items, itemsState);
        if (oldNode && isItemArrayDifferent) {
            // This is the case where an old node is modified.
            nodeIdCollectionChange.modified.push(nodeId);
        }
    });

    // Clean up any additional old node.
    additionalOldIds.forEach(additionalOldId => {
        // This is the case where an old node is removed.
        cleanUpConversationNode(additionalOldId, conversationNodesState, itemsState);
        nodeIdCollectionChange.removed.push(additionalOldId);
    });

    return nodeIdCollectionChange;
}

/** @returns whether item collection has been changed. */
function mergeItems(
    oldIds: string[],
    newItems: Item[],
    itemsState: ObservableMap<string, Item>
): boolean {
    const [isDifferent, additionalOldIds] = resetIdsIfDifferent(oldIds, newItems, itemIdProvider);

    // Merge each new item with the old.
    newItems.forEach(newItem => {
        const newItemId = newItem.ItemId.Id;
        const oldItem = itemsState.get(newItemId);

        if (!newItem.ContainsOnlyMandatoryProperties) {
            // Only cache new item from response if new item contains complete list of properties.
            // Preserve data in cache so that if we have some extra data that we want to persist doesn't get lost.
            // For eg. If we open item reading pane we have NormalizedBody in item, we would want to persist that information.
            if (oldItem) {
                preserveItemDataInCache(newItem, oldItem);
            } else {
                initializeSomeNonMandatoryItemProperties(newItem);
            }

            itemsState.set(newItemId, newItem);
        } else {
            if (oldItem) {
                // Update mandatory properties on old item if it exists in cache.
                itemMandatoryProperties.forEach(property => {
                    if (typeof newItem[property] !== 'undefined') {
                        oldItem[property] = newItem[property];
                    }
                });
            } else {
                // Remove item id from ConversationNode.ItemIds if the old item isn't cached and new item only contains mandatory properties from response.
                oldIds.splice(oldIds.indexOf(newItemId), 1);
            }
        }
    });

    // Clean up any additional old item.
    additionalOldIds.forEach(additionalOldId => {
        cleanUpItem(additionalOldId, itemsState);
    });

    return isDifferent;
}

function handleNodesWithDuplicateIds(
    conversationNodesState: ObservableMap<string, ConversationReadingPaneNode>,
    conversationNodes: ConversationNode[],
    conversationSortOrder: ConversationSortOrder,
    conversationId: string
) {
    const nodeIdMap: { [nodeId: string]: boolean } = {};
    const nodeCount = conversationNodes.length;
    const isNewestOnBottom = conversationSortOrder === 'ChronologicalNewestOnBottom';
    let index = isNewestOnBottom ? 0 : nodeCount - 1;
    const increment = isNewestOnBottom ? 1 : -1;
    while (index >= 0 && index < nodeCount) {
        const node = conversationNodes[index];
        const existingNode = conversationNodesState.get(node.InternetMessageId);
        if (
            (existingNode && existingNode.conversationId !== conversationId) ||
            nodeIdMap[node.InternetMessageId]
        ) {
            // If there's node with the same InternetMessageId from a different conversation or
            // there's a node with the same InternetMessageId in the same conversation,
            // then append ItemId to InternetMessageId to make it unique
            node.InternetMessageId += node.Items[0].ItemId.Id;

            // Update InternetMessageId of each item in the node
            node.Items.forEach(
                item => ((<Message>item).InternetMessageId = node.InternetMessageId)
            );
        } else {
            nodeIdMap[node.InternetMessageId] = true;
        }

        index += increment;
    }
}

/**
 * Calls postLoadConversation action if conversation is loaded on reading pane
 * @param conversationId
 * @param nodeIdCollectionChanged
 */
function updateReadingPaneStoreIfNecessary(
    conversationId: string,
    nodeIdCollectionChanged?: CollectionChange<string>
) {
    // Only update reading pane store if conversation is currently loaded.
    if (mailStore.mailModule.selectedId === conversationId) {
        lazyPostLoadConversation.importAndExecute(conversationId, nodeIdCollectionChanged);
    }
}

export interface ParseConversationResponseState {
    conversations: MruCache<ConversationItemParts>;
    conversationNodes: ObservableMap<string, ConversationReadingPaneNode>;
    items: ObservableMap<string, Item>;
}

export default action('parseConversationResponse')(
    select<ParseConversationResponseState>({
        conversations: () => conversationCache,
        conversationNodes: () => mailStore.conversationNodes,
        items: () => mailStore.items,
    })(function parseConversationResponse(
        responseMessage: GetConversationItemsResponseMessage,
        conversationSortOrder: ConversationSortOrder,
        conversationId: string,
        maxRetires: number,
        state?: ParseConversationResponseState
    ) {
        let conversationToLoad = state.conversations.get(conversationId);
        if (!conversationToLoad) {
            // If conversation is not in cache, it must be purged. In this case, we need to create an empty one.
            conversationToLoad = createEmptyConversationItemParts(
                conversationId,
                conversationSortOrder
            );
        }

        // Save empty conversationItemPart or touch conversationCache
        state.conversations.add(conversationId, conversationToLoad);

        // Property hasLoadState is notStarted implies we create an empty ConversationItemParts due to no cache or previous cache failed,
        // update hasLoadState if it's notStarted, otherwise, keep the cached value as is.
        const hasLoadSucceeded = responseMessage && responseMessage.ResponseClass === 'Success';
        if (conversationToLoad.loadingState.hasLoadState === HasLoadState.notStarted) {
            conversationToLoad.loadingState.hasLoadState = hasLoadSucceeded
                ? HasLoadState.hasSucceeded
                : HasLoadState.hasFailed;
        }

        if (conversationToLoad.loadingState.hasLoadState === HasLoadState.hasFailed) {
            // This happens when hasLoadState is originally notStarted and now hasFailed
            // hasLoadState originally cannot be hasFailed
            // since we always create an empty ConversationItemParts in such case
            updateReadingPaneStoreIfNecessary(conversationId);
        }

        // Parse success conversation response
        if (hasLoadSucceeded) {
            const conversationResponse = responseMessage.Conversation;

            if (!conversationResponse.ConversationNodes) {
                trace.trace.warn(
                    `GCI response has no ConversationNodes, but TotalConversationNodesCount is ${conversationResponse.TotalConversationNodesCount}`
                );

                if (maxRetires && conversationResponse.TotalConversationNodesCount) {
                    // Retry to loadConversation again, since ConversationNodes are null but TotalConversationNodesCount != 0
                    loadConversation(conversationId, maxRetires - 1);
                } else {
                    trace.errorThatWillCauseAlert(
                        `ParseConversationResponseError: GCI response has no ConversationNodes, but TotalConversationNodesCount is ${conversationResponse.TotalConversationNodesCount}`
                    );
                    conversationToLoad.loadingState.hasLoadState = HasLoadState.hasFailed;
                    updateReadingPaneStoreIfNecessary(conversationId);
                }

                return;
            }

            // Only update SyncState when the conversation node counts match.
            // When send mail to self, it is possible that the conversation table is updated according to sent item, but the message is not delivered yet.
            // In this case the syncState might contain new message id but GCI response doesn't really contain this conversationNode.
            if (
                conversationResponse.ConversationNodes.length ===
                conversationResponse.TotalConversationNodesCount
            ) {
                conversationToLoad.syncState = conversationResponse.SyncState;
            } else {
                const nodeCount = conversationResponse.ConversationNodes.length;
                const totalNodeCount = conversationResponse.TotalConversationNodesCount;
                trace.trace.warn(
                    `Count of conversationNodes (${nodeCount}) returned from GetConversationItems doesn't match TotalConversationNodesCount (${totalNodeCount})`
                );
            }

            // Update load more value
            conversationToLoad.canLoadMore =
                conversationToLoad.maxItemsToReturn < MAX_ITEMS_ALLOWED_TO_RETRIEVE &&
                conversationResponse.ConversationNodes &&
                !(
                    conversationResponse.ConversationNodes.length <
                    conversationToLoad.maxItemsToReturn
                );
            conversationToLoad.isLoadMoreInProgress = false;

            // Handle nodes with duplicate ids.
            // Append ItemId to InternetMessageId to make it unique whenever a duplicate InternetMessageId is detected.
            // This usually happens to system-generated emails wrongfully using the same InternetMessageId.
            // Note that it doesn't handle the case where the first node/conversation with duplicate InternetMessageId is hard deleted,
            // but it's a corner case and there's no other way to correlate.
            handleNodesWithDuplicateIds(
                state.conversationNodes,
                conversationResponse.ConversationNodes,
                conversationSortOrder,
                conversationId
            );

            // Merge nodes.
            const nodeIdCollectionChanged = mergeConversationNodes(
                conversationToLoad.conversationNodeIds,
                conversationResponse.ConversationNodes,
                state.conversationNodes,
                state.items,
                conversationId
            );

            // Notify reading pane to update view if it's a cached conversation and there's collection change.
            if (checkIfCollectionChanged(nodeIdCollectionChanged)) {
                updateReadingPaneStoreIfNecessary(conversationId, nodeIdCollectionChanged);
            }
        }
    })
);

const initializeSomeNonMandatoryItemProperties = (item: Item) => {
    // Initializing this property here to get tracked by mobx observer in 'ItemHeader' component
    (item as ClientItem).rsvpUnitAriaDetails = '';
};
